import React, { FC, ReactNode } from "react"
import { graphql, Link } from "gatsby"
import Markdown from "markdown-to-jsx"
import { renderRichText } from "gatsby-source-contentful/rich-text"

import { ContentPlusMediaBlockFragmentFragment } from "@graphqlTypes"

import { convertStringToKebabCase, generateConditionalClassName, processShortcodes } from "@utils"

import LpSection from "../LpSection/LpSection"
import Media from "../Media/Media"
import TextLink from "@components/TextLink/TextLink"

import "./ContentPlusMediaBlock.scss"

export enum MEDIA_POSITIONS {
  LEFT = "LEFT",
  RIGHT = "RIGHT",
}

export enum MEDIA_SIZES {
  DEFAULT = "DEFAULT",
  ONE_THIRD = "1/3 COL",
  TWO_THIRDS = "2/3 COL",
}

const MediaWithLink: FC<{
  children: ReactNode
  includeLink: boolean
  link?: string
}> = ({
  children,
  includeLink,
  link,
}) => {
    if (!includeLink || !link) return children

    const _link = processShortcodes(link)

    if (_link.includes("//")) return (
      <a href={_link}>{children}</a>
    )

    return (
      <Link to={_link}>{children}</Link>
    )
  }

const ContentPlusMediaBlock: FC<ContentPlusMediaBlockFragmentFragment> = ({
  contentful_id,
  background,
  image,
  includeBackground,
  includeMediaBoxShadow,
  listItems,
  marginBottom,
  mediaPosition,
  mediaSize,
  playVideoInPnpPlayer,
  playVideoInline,
  sectionText,
  sectionTitle,
  subhead,
  text,
  textLinkLabel,
  textLinkUrl,
  title,
  useDarkMode,
  wistiaId,
}) => {
  const hasListItems = !!listItems && listItems.length > 0

  const _contentClassName = generateConditionalClassName({
    "row--1": mediaPosition === MEDIA_POSITIONS.RIGHT,
    "row--2": mediaPosition === MEDIA_POSITIONS.LEFT,
    // MEDIA_SIZES.DEFAULT
    "column--2-6-L":
      !hasListItems &&
      mediaSize === MEDIA_SIZES.DEFAULT &&
      mediaPosition === MEDIA_POSITIONS.RIGHT,
    "column--8-12-L":
      !hasListItems &&
      mediaSize === MEDIA_SIZES.DEFAULT &&
      mediaPosition === MEDIA_POSITIONS.LEFT,
    "column--1-7-L":
      hasListItems &&
      mediaSize === MEDIA_SIZES.DEFAULT &&
      mediaPosition === MEDIA_POSITIONS.RIGHT,
    "column--7-13-L":
      hasListItems &&
      mediaSize === MEDIA_SIZES.DEFAULT &&
      mediaPosition === MEDIA_POSITIONS.LEFT,
    // MEDIA_SIZES.ONE_THIRD
    "column--6-11-L":
      !hasListItems &&
      mediaSize === MEDIA_SIZES.ONE_THIRD &&
      mediaPosition === MEDIA_POSITIONS.LEFT,
    "column--2-7-L":
      !hasListItems &&
      mediaSize === MEDIA_SIZES.ONE_THIRD &&
      mediaPosition === MEDIA_POSITIONS.RIGHT,
    "column--6-12-L padding-left--M":
      hasListItems &&
      mediaSize === MEDIA_SIZES.ONE_THIRD &&
      mediaPosition === MEDIA_POSITIONS.LEFT,
    "column--1-8-L padding-right--M":
      hasListItems &&
      mediaSize === MEDIA_SIZES.ONE_THIRD &&
      mediaPosition === MEDIA_POSITIONS.RIGHT,
    // MEDIA_SIZES.TWO_THIRDS
    "column--9-13-L":
      !hasListItems &&
      mediaSize === MEDIA_SIZES.TWO_THIRDS &&
      mediaPosition === MEDIA_POSITIONS.LEFT,
    "column--1-5-L":
      !hasListItems &&
      mediaSize === MEDIA_SIZES.TWO_THIRDS &&
      mediaPosition === MEDIA_POSITIONS.RIGHT,
    "column--8-13-L padding-left--M":
      hasListItems &&
      mediaSize === MEDIA_SIZES.TWO_THIRDS &&
      mediaPosition === MEDIA_POSITIONS.LEFT,
    "column--1-6-L padding-right--M":
      hasListItems &&
      mediaSize === MEDIA_SIZES.TWO_THIRDS &&
      mediaPosition === MEDIA_POSITIONS.RIGHT,
  })

  const _mediaClassName = generateConditionalClassName({
    "row--1": mediaPosition === MEDIA_POSITIONS.LEFT,
    "row--2": mediaPosition === MEDIA_POSITIONS.RIGHT,
    "column--1-7-L":
      mediaSize === MEDIA_SIZES.DEFAULT &&
      mediaPosition === MEDIA_POSITIONS.LEFT,
    "column--7-13-L":
      mediaSize === MEDIA_SIZES.DEFAULT &&
      mediaPosition === MEDIA_POSITIONS.RIGHT,
    "column--1-5-L":
      mediaSize === MEDIA_SIZES.ONE_THIRD &&
      mediaPosition === MEDIA_POSITIONS.LEFT,
    "column--9-13-L":
      mediaSize === MEDIA_SIZES.ONE_THIRD &&
      mediaPosition === MEDIA_POSITIONS.RIGHT,
    "column--1-8-L":
      mediaSize === MEDIA_SIZES.TWO_THIRDS &&
      mediaPosition === MEDIA_POSITIONS.LEFT,
    "column--6-13-L":
      mediaSize === MEDIA_SIZES.TWO_THIRDS &&
      mediaPosition === MEDIA_POSITIONS.RIGHT,
  })

  return (
    <LpSection
      background={background}
      className="ContentPlusMediaBlock"
      includeBackground={includeBackground}
      marginBottom={marginBottom}
      text={sectionText}
      title={sectionTitle}
      uniqueId={contentful_id}
      useDarkMode={useDarkMode}
    >
      <div className="container">
        <div className="grid">
          <div
            className={`ContentPlusMediaBlock--content column column--1-7-XS column--2-12-M ${_contentClassName}`}
          >
            {(title || text?.raw) && (
              <>
                {subhead && (
                  <p className="overline margin-bottom--S type--mono-S color--text-light">
                    {subhead}
                  </p>
                )}
                <h3 id={convertStringToKebabCase(title || "")}>{title}</h3>
                {text &&
                  typeof text.raw === "string" &&
                  renderRichText({ raw: text.raw, references: [] })}
              </>
            )}
            {hasListItems && listItems.length > 0 && (
              <ul className="ListItems">
                {listItems.map((listItem) =>
                  listItem?.title && listItem.bodyText ? (
                    <li key={listItem.title}>
                      <h6>{listItem.title}</h6>
                      <p>
                        <Markdown>{listItem.bodyText}</Markdown>
                      </p>
                    </li>
                  ) : null
                )}
              </ul>
            )}

            {textLinkLabel && textLinkUrl && (
              <div className="margin-top--S">
                <TextLink href={textLinkUrl}>{textLinkLabel}</TextLink>
              </div>
            )}
          </div>
          <div
            className={`ContentPlusMediaBlock--media column column--1-7-XS column--2-12-M ${_mediaClassName}`}
          >
            <MediaWithLink
              includeLink={!(wistiaId && !playVideoInline)}
              link={textLinkUrl || "/"}
            >
              <Media
                {...{
                  image,
                  includeMediaBoxShadow,
                  playVideoInline,
                  playVideoInPnpPlayer,
                  wistiaId,
                }}
              />
            </MediaWithLink>
          </div>
        </div>
      </div>
    </LpSection>
  )
}

export const ContentPlusMediaBlockFragment = graphql`
  fragment ContentPlusMediaBlockFragment on ContentfulLpContentPlusMediaBlock {
    ...ContentPlusMediaBlockMediaFragment
    contentful_id
    background
    includeBackground
    marginBottom
    useDarkMode
    title
    subhead
    sectionTitle
    sectionText {
      raw
    }
    text {
      raw
    }
    textLinkLabel
    textLinkUrl
    listItems {
      bodyText
      title
    }
    mediaPosition
    mediaSize
  }
`
export default ContentPlusMediaBlock
